import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const categorySchema = z.object({
  name: z.string().min(1, "Name is required"),
  level: z.enum(["1", "2", "3"], {
    required_error: "Level is required",
    invalid_type_error: "Level must be one of: 1, 2, 3",
  }),
});

type CategoryFormValues = z.infer<typeof categorySchema>;

interface CategoryFormProps {
  id?: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const CategoriesForm = ({
  id,
  onSuccess,
  onCancel,
}: CategoryFormProps) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
    },
  });

  // Fetch supplier data if in edit mode
  useQuery({
    queryKey: ["supplier", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`categories/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch supplier");
      }
      const data = await response.json();
      form.reset(data);
      return data;
    },
    enabled: isEdit && !!id,
  });

  const mutation = useMutation({
    mutationFn: async (data: CategoryFormValues) => {
      const url = isEdit ? `categories/${id}/` : "categories/";
      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(getApiUrl(url), {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save supplier");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      onSuccess();
    },
  });

  const onSubmit = async (data: CategoryFormValues) => {
    try {
      await mutation.mutateAsync(data);
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("categories.form.name")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("categories.form.namePlaceholder")}
                  {...field}
                  disabled={mutation.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="level"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("categories.form.level")}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                disabled={mutation.isPending}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("categories.form.selectLevel")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? t("common.saving")
              : isEdit
                ? t("common.update")
                : t("common.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
